import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp,
  Speed,
  Assessment,
  Download,
  Schedule,
  Add,
  Description,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
} from 'recharts';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

const PIE_COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#e91e63'];

// ─── Date helpers ──────────────────────────────────────────────────────────────
function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}
function thirtyDaysAgoStr(): string {
  return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
}

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface OeeData {
  oee?: number;
  availability?: number;
  performance?: number;
  quality?: number;
  date?: string;
  period?: string;
  [key: string]: any;
}

interface TrendPoint {
  date?: string;
  day?: string;
  produced?: number;
  planned?: number;
  efficiency?: number;
  quality?: number;
  [key: string]: any;
}

interface WorkcenterOee {
  id?: string;
  workCenter?: string;
  name?: string;
  oee?: number;
  availability?: number;
  performance?: number;
  quality?: number;
  [key: string]: any;
}

interface QualityData {
  qualityRate?: number;
  passCount?: number;
  failCount?: number;
  defectsByType?: Array<{ name: string; value: number }>;
  trends?: TrendPoint[];
  [key: string]: any;
}

interface ProductionData {
  totalProduced?: number;
  planned?: number;
  efficiency?: number;
  [key: string]: any;
}

interface ForecastPoint {
  date?: string;
  forecast?: number;
  actual?: number;
  [key: string]: any;
}

interface AnomalyRow {
  id?: string | number;
  itemId?: string;
  timestamp?: string;
  metric?: string;
  value?: number;
  severity?: string;
  [key: string]: any;
}

interface Report {
  id: string;
  reportName?: string;
  name?: string;
  reportType?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  reportFormat?: string;
  format?: string;
  scheduleFrequency?: string;
  [key: string]: any;
}

// ─── Utility: safe fetch ──────────────────────────────────────────────────────
async function apiFetch(path: string, options?: RequestInit): Promise<any> {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!resp.ok) throw { message: `HTTP ${resp.status}`, status: resp.status };
  return resp.json();
}

// ─── OEE Gauge using RadialBarChart ──────────────────────────────────────────
const OeeGauge: React.FC<{ value: number; label: string; color: string }> = ({
  value,
  label,
  color,
}) => {
  const data = [{ name: label, value: Math.min(100, Math.max(0, value || 0)), fill: color }];
  return (
    <Box textAlign="center">
      <ResponsiveContainer width="100%" height={160}>
        <RadialBarChart
          cx="50%"
          cy="80%"
          innerRadius="60%"
          outerRadius="100%"
          barSize={16}
          data={data}
          startAngle={180}
          endAngle={0}
        >
          <RadialBar background dataKey="value" />
        </RadialBarChart>
      </ResponsiveContainer>
      <Typography variant="h4" color={color} fontWeight="bold" mt={-4}>
        {value != null ? `${Number(value).toFixed(1)}%` : '--'}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
    </Box>
  );
};

// ─── Date Range Filter ────────────────────────────────────────────────────────
interface DateRangeFilterProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onApply: () => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onApply,
}) => (
  <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
    <TextField
      label="From"
      type="date"
      size="small"
      value={dateFrom}
      onChange={(e) => onDateFromChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      label="To"
      type="date"
      size="small"
      value={dateTo}
      onChange={(e) => onDateToChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <Button variant="contained" size="small" onClick={onApply}>
      Apply
    </Button>
  </Box>
);

// ─── Workcenter Drill-down Dialog ─────────────────────────────────────────────
interface WorkcenterDetailDialogProps {
  open: boolean;
  workcenter: WorkcenterOee | null;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}

const WorkcenterDetailDialog: React.FC<WorkcenterDetailDialogProps> = ({
  open,
  workcenter,
  dateFrom,
  dateTo,
  onClose,
}) => {
  const [detail, setDetail] = useState<OeeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !workcenter) return;
    const wcId = workcenter.id ?? workcenter.workCenter ?? workcenter.name;
    if (!wcId) return;
    setLoading(true);
    setError(null);
    apiFetch(
      `/api/v1/analytics/oee?workCenterId=${encodeURIComponent(wcId)}&from=${dateFrom}&to=${dateTo}`,
    )
      .then((resp) => {
        setDetail(resp?.data ?? resp);
      })
      .catch((e: any) => {
        setError(e.message || 'Failed to load workcenter details');
      })
      .finally(() => setLoading(false));
  }, [open, workcenter, dateFrom, dateTo]);

  const name = workcenter?.workCenter ?? workcenter?.name ?? 'Work Center';

  const metrics = detail
    ? [
        { label: 'OEE', value: detail.oee ?? 0, color: '#1976d2' },
        { label: 'Availability', value: detail.availability ?? 0, color: '#4caf50' },
        { label: 'Performance', value: detail.performance ?? 0, color: '#2196f3' },
        { label: 'Quality', value: detail.quality ?? 0, color: '#ff9800' },
      ]
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>OEE Detail — {name}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {!loading && !error && detail && (
          <Box mt={1}>
            {metrics.map((m) => (
              <Box key={m.label} mb={2}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">{m.label}</Typography>
                  <Typography variant="body2" fontWeight="bold" color={m.color}>
                    {Number(m.value).toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, m.value))}
                  sx={{ height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { bgcolor: m.color } }}
                />
              </Box>
            ))}
          </Box>
        )}
        {!loading && !error && !detail && (
          <Alert severity="info">No detail data available for this work center.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Tab 1: OEE Dashboard ─────────────────────────────────────────────────────
const OeeDashboard: React.FC = () => {
  const [oee, setOee] = useState<OeeData | null>(null);
  const [trends, setTrends] = useState<OeeData[]>([]);
  const [workcenters, setWorkcenters] = useState<WorkcenterOee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgoStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [appliedFrom, setAppliedFrom] = useState(thirtyDaysAgoStr());
  const [appliedTo, setAppliedTo] = useState(todayStr());
  const [selectedWC, setSelectedWC] = useState<WorkcenterOee | null>(null);
  const [wcDialogOpen, setWcDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `?from=${appliedFrom}&to=${appliedTo}`;
      const [oeeData, wcData] = await Promise.all([
        apiFetch(`/api/v1/analytics/oee${qs}`).catch(() => null),
        apiFetch(`/api/v1/analytics/comparison/workcenters${qs}`).catch(() => null),
      ]);
      if (oeeData) {
        const d = oeeData.data ?? oeeData;
        setOee(d);
        if (Array.isArray(d.trends)) setTrends(d.trends);
        else if (Array.isArray(d)) setTrends(d);
      }
      if (wcData) {
        const list = wcData.data ?? wcData;
        setWorkcenters(Array.isArray(list) ? list : []);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load OEE data');
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  const handleWCClick = (wc: WorkcenterOee) => {
    setSelectedWC(wc);
    setWcDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Could not load OEE data from analytics service: {error}. Displaying layout with no data.
      </Alert>
    );
  }

  const overallOee = oee?.oee ?? oee?.overall ?? 0;
  const availability = oee?.availability ?? 0;
  const performance = oee?.performance ?? 0;
  const quality = oee?.quality ?? 0;

  const trendData: OeeData[] =
    trends.length > 0
      ? trends
      : Array.from({ length: 30 }, (_, i) => ({
          date: `Day ${i + 1}`,
          oee: 0,
          availability: 0,
          performance: 0,
          quality: 0,
        }));

  const wcData: WorkcenterOee[] = workcenters.length > 0 ? workcenters : [];

  return (
    <Box>
      <DateRangeFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={handleApply}
      />

      {/* Top summary cards */}
      <Grid container spacing={3} mb={3}>
        {[
          { label: 'Overall OEE', value: overallOee, color: 'primary.main' },
          { label: 'Availability', value: availability, color: 'success.main' },
          { label: 'Performance', value: performance, color: 'info.main' },
          { label: 'Quality', value: quality, color: 'warning.main' },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {item.label}
                </Typography>
                <Typography variant="h4" color={item.color}>
                  {item.value != null ? `${Number(item.value).toFixed(1)}%` : '--'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mb={3}>
        {/* OEE Gauge */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              OEE Breakdown
            </Typography>
            <Grid container>
              <Grid item xs={12}>
                <OeeGauge value={overallOee} label="Overall OEE" color="#1976d2" />
              </Grid>
              <Grid item xs={4}>
                <OeeGauge value={availability} label="Avail." color="#4caf50" />
              </Grid>
              <Grid item xs={4}>
                <OeeGauge value={performance} label="Perf." color="#2196f3" />
              </Grid>
              <Grid item xs={4}>
                <OeeGauge value={quality} label="Qual." color="#ff9800" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* OEE trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="h6" gutterBottom>
              OEE Trend
            </Typography>
            {trendData.every((d) => !d.oee) ? (
              <Alert severity="info">No trend data available from the analytics service.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="oee" stroke="#1976d2" strokeWidth={2} name="OEE" dot={false} />
                  <Line type="monotone" dataKey="availability" stroke="#4caf50" strokeWidth={1} name="Availability" dot={false} />
                  <Line type="monotone" dataKey="performance" stroke="#2196f3" strokeWidth={1} name="Performance" dot={false} />
                  <Line type="monotone" dataKey="quality" stroke="#ff9800" strokeWidth={1} name="Quality" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Work Center comparison */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Work Center OEE Comparison{' '}
          <Typography component="span" variant="caption" color="textSecondary">
            (click a row for details)
          </Typography>
        </Typography>
        {wcData.length === 0 ? (
          <Alert severity="info">No work center comparison data available.</Alert>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={wcData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="workCenter" />
                <YAxis domain={[0, 100]} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="oee" fill="#1976d2" name="OEE %" />
                <Bar dataKey="availability" fill="#4caf50" name="Availability %" />
                <Bar dataKey="performance" fill="#2196f3" name="Performance %" />
              </BarChart>
            </ResponsiveContainer>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Work Center</TableCell>
                    <TableCell align="right">OEE %</TableCell>
                    <TableCell align="right">Availability %</TableCell>
                    <TableCell align="right">Performance %</TableCell>
                    <TableCell align="right">Quality %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wcData.map((wc, idx) => (
                    <TableRow
                      key={wc.id ?? wc.workCenter ?? idx}
                      hover
                      onClick={() => handleWCClick(wc)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{wc.workCenter ?? wc.name ?? '--'}</TableCell>
                      <TableCell align="right">{wc.oee != null ? `${Number(wc.oee).toFixed(1)}%` : '--'}</TableCell>
                      <TableCell align="right">{wc.availability != null ? `${Number(wc.availability).toFixed(1)}%` : '--'}</TableCell>
                      <TableCell align="right">{wc.performance != null ? `${Number(wc.performance).toFixed(1)}%` : '--'}</TableCell>
                      <TableCell align="right">{wc.quality != null ? `${Number(wc.quality).toFixed(1)}%` : '--'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <WorkcenterDetailDialog
        open={wcDialogOpen}
        workcenter={selectedWC}
        dateFrom={appliedFrom}
        dateTo={appliedTo}
        onClose={() => setWcDialogOpen(false)}
      />
    </Box>
  );
};

// ─── Tab 2: Production Analytics ─────────────────────────────────────────────
const ProductionAnalytics: React.FC = () => {
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [today, setToday] = useState<ProductionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgoStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [appliedFrom, setAppliedFrom] = useState(thirtyDaysAgoStr());
  const [appliedTo, setAppliedTo] = useState(todayStr());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `?from=${appliedFrom}&to=${appliedTo}`;
      const [trendData, productionData] = await Promise.all([
        apiFetch(`/api/v1/analytics/trends/production${qs}`).catch(() => null),
        apiFetch(`/api/v1/analytics/production${qs}`).catch(() => null),
      ]);
      if (trendData) {
        const list = trendData.data ?? trendData;
        setTrends(Array.isArray(list) ? list : []);
      }
      if (productionData) {
        setToday(productionData.data ?? productionData);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load production data');
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DateRangeFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={handleApply}
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load production data: {error}
        </Alert>
      )}

      {/* Today's KPI cards */}
      <Grid container spacing={3} mb={3}>
        {[
          {
            label: "Today's Produced",
            value: today?.totalProduced != null ? String(today.totalProduced) : '--',
            color: 'primary.main',
          },
          {
            label: 'Planned',
            value: today?.planned != null ? String(today.planned) : '--',
            color: 'info.main',
          },
          {
            label: 'Efficiency',
            value: today?.efficiency != null ? `${Number(today.efficiency).toFixed(1)}%` : '--',
            color: 'success.main',
          },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {item.label}
                </Typography>
                <Typography variant="h4" color={item.color}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Production trends */}
      <Paper sx={{ p: 2, mb: 3, height: 360 }}>
        <Typography variant="h6" gutterBottom>
          Production Trends
        </Typography>
        {trends.length === 0 ? (
          <Alert severity="info">No trend data available from the analytics service.</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="85%">
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
              <RechartsTooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="produced" stroke="#1976d2" strokeWidth={2} name="Produced" dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="planned" stroke="#9e9e9e" strokeWidth={1} strokeDasharray="5 5" name="Planned" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="efficiency" stroke="#4caf50" strokeWidth={2} name="Efficiency %" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="quality" stroke="#ff9800" strokeWidth={2} name="Quality %" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

// ─── Tab 3: Quality Analytics ─────────────────────────────────────────────────
const QualityAnalytics: React.FC = () => {
  const [data, setData] = useState<QualityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgoStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [appliedFrom, setAppliedFrom] = useState(thirtyDaysAgoStr());
  const [appliedTo, setAppliedTo] = useState(todayStr());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `?from=${appliedFrom}&to=${appliedTo}`;
      const resp = await apiFetch(`/api/v1/analytics/quality${qs}`);
      setData(resp.data ?? resp);
    } catch (e: any) {
      setError(e.message || 'Failed to load quality data');
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const qualityRate = data?.qualityRate ?? 0;
  const passCount = data?.passCount ?? 0;
  const failCount = data?.failCount ?? 0;
  const defects: Array<{ name: string; value: number }> = data?.defectsByType ?? [];
  const qualityTrend: TrendPoint[] = data?.trends ?? [];

  return (
    <Box>
      <DateRangeFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={handleApply}
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load quality data: {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={3} mb={3}>
        {[
          { label: 'Quality Rate', value: `${Number(qualityRate).toFixed(1)}%`, color: 'success.main' },
          { label: 'Pass Count', value: String(passCount), color: 'primary.main' },
          { label: 'Fail Count', value: String(failCount), color: 'error.main' },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {item.label}
                </Typography>
                <Typography variant="h4" color={item.color}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quality rate trend */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="h6" gutterBottom>
              Quality Rate Trend
            </Typography>
            {qualityTrend.length === 0 ? (
              <Alert severity="info">No quality trend data available.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={qualityTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="quality" stroke="#4caf50" strokeWidth={2} name="Quality Rate %" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Defects by type pie */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="h6" gutterBottom>
              Defects by Type
            </Typography>
            {defects.length === 0 ? (
              <Alert severity="info">No defect breakdown data available.</Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height="65%">
                  <PieChart>
                    <Pie data={defects} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={3} dataKey="value">
                      {defects.map((_, idx) => (
                        <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box>
                  {defects.map((d, idx) => (
                    <Box key={d.name} display="flex" justifyContent="space-between" mb={0.5}>
                      <Box display="flex" alignItems="center">
                        <Box width={10} height={10} bgcolor={PIE_COLORS[idx % PIE_COLORS.length]} borderRadius="50%" mr={1} />
                        <Typography variant="caption">{d.name}</Typography>
                      </Box>
                      <Typography variant="caption" fontWeight="bold">
                        {d.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Tab 4: AI Insights ───────────────────────────────────────────────────────
const AiInsights: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<string>('ITEM-001');
  const [forecast, setForecast] = useState<ForecastPoint[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyRow[]>([]);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [forecastError, setForecastError] = useState<string | null>(null);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);

  const items = ['ITEM-001', 'ITEM-002', 'ITEM-003', 'ITEM-004', 'ITEM-005'];

  const loadForecast = async (itemId: string) => {
    setForecastLoading(true);
    setForecastError(null);
    try {
      const resp = await apiFetch(`/api/v1/analytics/ai/demand-forecast?itemId=${itemId}`);
      const list = resp.data ?? resp;
      setForecast(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setForecastError(e.message || 'Failed to load demand forecast');
    } finally {
      setForecastLoading(false);
    }
  };

  const loadAnomalies = async () => {
    setAnomalyLoading(true);
    setAnomalyError(null);
    try {
      const resp = await apiFetch('/api/v1/analytics/ai/anomaly-detection');
      const list = resp.data ?? resp;
      setAnomalies(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setAnomalyError(e.message || 'Failed to load anomaly data');
    } finally {
      setAnomalyLoading(false);
    }
  };

  useEffect(() => {
    loadForecast(selectedItem);
    loadAnomalies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemChange = (itemId: string) => {
    setSelectedItem(itemId);
    loadForecast(itemId);
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
      case 'warning':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Demand forecast */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Demand Forecast</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Item</InputLabel>
            <Select value={selectedItem} label="Item" onChange={(e) => handleItemChange(e.target.value)}>
              {items.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {forecastLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : forecastError ? (
          <Alert severity="warning">{forecastError}</Alert>
        ) : forecast.length === 0 ? (
          <Alert severity="info">No forecast data available for {selectedItem}.</Alert>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={forecast}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Line type="monotone" dataKey="forecast" stroke="#9c27b0" strokeWidth={2} name="Forecast" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="actual" stroke="#1976d2" strokeWidth={2} name="Actual" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Anomaly detection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Anomaly Detection Results
        </Typography>

        {anomalyLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : anomalyError ? (
          <Alert severity="warning">{anomalyError}</Alert>
        ) : anomalies.length === 0 ? (
          <Alert severity="success">No anomalies detected in recent data.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item / Equipment</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Metric</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Severity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anomalies.map((row) => (
                  <TableRow key={row.id ?? Math.random()}>
                    <TableCell>{row.itemId ?? row.equipmentId ?? '--'}</TableCell>
                    <TableCell>{row.timestamp ? new Date(row.timestamp).toLocaleString() : '--'}</TableCell>
                    <TableCell>{row.metric ?? '--'}</TableCell>
                    <TableCell>{row.value != null ? row.value : '--'}</TableCell>
                    <TableCell>
                      <Chip
                        label={(row.severity ?? 'unknown').toUpperCase()}
                        color={getSeverityColor(row.severity ?? '')}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// ─── Tab 5: Reports ──────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { value: 'oee_summary', label: 'OEE Summary' },
  { value: 'defect_analysis', label: 'Defect Analysis' },
  { value: 'production_summary', label: 'Production Summary' },
  { value: 'maintenance_summary', label: 'Maintenance Summary' },
];

const REPORT_FORMATS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'excel', label: 'Excel' },
  { value: 'csv', label: 'CSV' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

interface GenerateReportForm {
  name: string;
  type: string;
  dateFrom: string;
  dateTo: string;
  format: string;
}

interface ScheduleForm {
  frequency: string;
  recipients: string;
}

const ReportsTab: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notAvailable, setNotAvailable] = useState(false);

  // Generate dialog
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState<GenerateReportForm>({
    name: '',
    type: 'oee_summary',
    dateFrom: thirtyDaysAgoStr(),
    dateTo: todayStr(),
    format: 'pdf',
  });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Schedule dialog
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleReportId, setScheduleReportId] = useState<string | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>({ frequency: 'daily', recipients: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    setNotAvailable(false);
    try {
      const resp = await apiFetch('/api/v1/analytics/reports');
      const list = resp.data ?? resp;
      setReports(Array.isArray(list) ? list : []);
    } catch (e: any) {
      if (e.status === 404) {
        setNotAvailable(true);
      } else {
        setError(e.message || 'Failed to load reports');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = (id: string) => {
    const token = localStorage.getItem('token');
    const url = `${API}/api/v1/analytics/reports/${id}/download`;
    // Open in new tab with token param fallback for file downloads
    const anchor = document.createElement('a');
    anchor.href = `${url}?token=${encodeURIComponent(token ?? '')}`;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  };

  const handleOpenSchedule = (id: string) => {
    setScheduleReportId(id);
    setScheduleForm({ frequency: 'daily', recipients: '' });
    setScheduleError(null);
    setScheduleOpen(true);
  };

  const handleGenerateSubmit = async () => {
    if (!generateForm.name.trim()) {
      setGenerateError('Report name is required.');
      return;
    }
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      await apiFetch('/api/v1/analytics/reports/generate', {
        method: 'POST',
        body: JSON.stringify({
          name: generateForm.name,
          type: generateForm.type,
          dateFrom: generateForm.dateFrom,
          dateTo: generateForm.dateTo,
          format: generateForm.format,
        }),
      });
      setGenerateOpen(false);
      setSnackMsg('Report generation started');
      setSnackOpen(true);
      loadReports();
    } catch (e: any) {
      if (e.status === 404) {
        setGenerateError('Reports module not yet available on the backend.');
      } else {
        setGenerateError(e.message || 'Failed to generate report');
      }
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleReportId) return;
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      await apiFetch(`/api/v1/analytics/reports/${scheduleReportId}/schedule`, {
        method: 'POST',
        body: JSON.stringify({
          frequency: scheduleForm.frequency,
          recipients: scheduleForm.recipients
            .split(',')
            .map((r) => r.trim())
            .filter(Boolean),
        }),
      });
      setScheduleOpen(false);
      setSnackMsg('Report scheduled successfully');
      setSnackOpen(true);
      loadReports();
    } catch (e: any) {
      if (e.status === 404) {
        setScheduleError('Schedule feature not yet available on the backend.');
      } else {
        setScheduleError(e.message || 'Failed to schedule report');
      }
    } finally {
      setScheduleLoading(false);
    }
  };

  const getStatusColor = (status?: string): 'success' | 'warning' | 'default' | 'info' | 'error' => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      case 'inactive':
        return 'error';
      default:
        return 'info';
    }
  };

  if (notAvailable) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Reports module not yet available. The backend team will implement this feature soon.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Saved & Scheduled Reports</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setGenerateForm({ name: '', type: 'oee_summary', dateFrom: thirtyDaysAgoStr(), dateTo: todayStr(), format: 'pdf' });
            setGenerateError(null);
            setGenerateOpen(true);
          }}
        >
          Generate Report
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Description sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="textSecondary">No reports generated yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Report Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Generated At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.reportName ?? report.name ?? '--'}</TableCell>
                  <TableCell>{report.reportType ?? report.type ?? '--'}</TableCell>
                  <TableCell>
                    {report.createdAt ? new Date(report.createdAt).toLocaleString() : '--'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(report.status ?? 'unknown').toUpperCase()}
                      color={getStatusColor(report.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(report.id)}>
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Schedule">
                      <IconButton size="small" onClick={() => handleOpenSchedule(report.id)}>
                        <Schedule />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Generate Report Dialog */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {generateError && <Alert severity="error">{generateError}</Alert>}
            <TextField
              label="Report Name"
              required
              fullWidth
              value={generateForm.name}
              onChange={(e) => setGenerateForm((f) => ({ ...f, name: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={generateForm.type}
                label="Report Type"
                onChange={(e) => setGenerateForm((f) => ({ ...f, type: e.target.value }))}
              >
                {REPORT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Date From"
              type="date"
              fullWidth
              value={generateForm.dateFrom}
              onChange={(e) => setGenerateForm((f) => ({ ...f, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date To"
              type="date"
              fullWidth
              value={generateForm.dateTo}
              onChange={(e) => setGenerateForm((f) => ({ ...f, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={generateForm.format}
                label="Format"
                onChange={(e) => setGenerateForm((f) => ({ ...f, format: e.target.value }))}
              >
                {REPORT_FORMATS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generateLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateSubmit} disabled={generateLoading}>
            {generateLoading ? <CircularProgress size={20} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Report</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {scheduleError && <Alert severity="error">{scheduleError}</Alert>}
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={scheduleForm.frequency}
                label="Frequency"
                onChange={(e) => setScheduleForm((f) => ({ ...f, frequency: e.target.value }))}
              >
                {FREQUENCIES.map((fr) => (
                  <MenuItem key={fr.value} value={fr.value}>
                    {fr.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Recipients (comma-separated emails)"
              fullWidth
              value={scheduleForm.recipients}
              onChange={(e) => setScheduleForm((f) => ({ ...f, recipients: e.target.value }))}
              placeholder="user@example.com, other@example.com"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)} disabled={scheduleLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleScheduleSubmit} disabled={scheduleLoading}>
            {scheduleLoading ? <CircularProgress size={20} /> : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
      />
    </Box>
  );
};

// ─── Main Analytics component ─────────────────────────────────────────────────
const Analytics: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={3}>
        Analytics &amp; AI
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="OEE Dashboard" icon={<Speed />} iconPosition="start" />
          <Tab label="Production Analytics" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Quality Analytics" icon={<Assessment />} iconPosition="start" />
          <Tab label="AI Insights" icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab label="Reports" icon={<Description />} iconPosition="start" />
        </Tabs>
      </Paper>

      {selectedTab === 0 && <OeeDashboard />}
      {selectedTab === 1 && <ProductionAnalytics />}
      {selectedTab === 2 && <QualityAnalytics />}
      {selectedTab === 3 && <AiInsights />}
      {selectedTab === 4 && <ReportsTab />}
    </Box>
  );
};

export default Analytics;
